import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { __t } from '../../../i18n/t';
import { HomeItem, ROW_TITLE_MAX, SECTION_CONTENT_HINT } from './homeItems';

interface SortableRowProps {
  item: HomeItem;
  sectionLabel: string;
  /** Texto que o build mostra. Ausente = a seção não tem título para renomear. */
  defaultTitle?: string;
  onToggleVisible(): void;
  onChangeTitle(title: string): void;
}

const DRAG_HANDLE_SX = {
  display: 'flex', alignItems: 'center', cursor: 'grab',
  color: 'text.disabled', touchAction: 'none',
};

/**
 * Içado porque o elemento é renderizado dentro de um `{cond && <JSX/>}`: ali o
 * plugin de i18n embrulha os VALORES do `sx` (`__t_i18n("flex")`), e bastaria
 * alguém traduzir "flex" num lote para o layout quebrar em espanhol. Em atributo
 * solto isso não acontece — por isso os outros `sx` deste arquivo estão inline.
 *
 * `pl` alinha o campo com o nome da seção, descontando a alça de arrasto.
 */
const EDIT_SX = { display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5, pl: 3.5 };

/** Linha de SEÇÃO: vem do build do app, então só move, oculta e renomeia o título. */
const SortableRow: React.FC<SortableRowProps> = ({
  item, sectionLabel, defaultTitle, onToggleVisible, onChangeTitle,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, data: { type: 'section' } });
  const hint = SECTION_CONTENT_HINT[item.id];

  // Rascunho local: o título só vai para o formulário ao confirmar, para que desistir
  // no meio da edição devolva o que estava salvo.
  const [draft, setDraft] = useState<string | null>(null);
  const editing = draft !== null;

  const save = () => {
    onChangeTitle((draft || '').trim());
    setDraft(null);
  };

  // Fora do JSX de propósito: o plugin de i18n embrulha literal em posição de prop, e
  // um 'Enter' traduzido faria o atalho parar de funcionar em espanhol.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') { event.preventDefault(); save(); }
    if (event.key === 'Escape') { setDraft(null); }
  };

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        px: 1, py: 0.75, mb: 0.75,
        opacity: isDragging ? 0.4 : (item.visible ? 1 : 0.55),
        bgcolor: 'action.hover',
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box {...attributes} {...listeners} sx={DRAG_HANDLE_SX}>
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>{__t(sectionLabel)}</Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {__t(hint)}
            </Typography>
          )}
          {/* Renomeado aparece na própria lista: é o que permite varrer de relance o que
              foi mexido, sem abrir seção por seção. */}
          {!editing && !!item.title && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {__t('No app: %{titulo}', { titulo: item.title })}
            </Typography>
          )}
        </Box>

        {defaultTitle && !editing && (
          <Tooltip title="Renomear o título desta seção">
            <IconButton size="small" onClick={() => setDraft(item.title || '')}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={item.visible ? 'Esconder da Home' : 'Mostrar na Home'}>
          <IconButton size="small" onClick={onToggleVisible}>
            {item.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {editing && defaultTitle && (
        <Box sx={EDIT_SX}>
          <TextField
            size="small"
            variant="standard"
            autoFocus
            fullWidth
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{ maxLength: ROW_TITLE_MAX }}
            helperText={__t('Em branco, o app mostra “%{padrao}”.', { padrao: defaultTitle })}
          />
          <Tooltip title="Salvar título">
            <IconButton size="small" onClick={save}><CheckIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Cancelar">
            <IconButton size="small" onClick={() => setDraft(null)}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

export default SortableRow;
